import { useEffect, useState } from "react"
import {
  Box,
  Button,
  FormControl,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Card,
  CardContent,
  Paper,
  IconButton,
  Tooltip,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  TableContainer,
  Table,
  CircularProgress,
  FormHelperText
} from "@mui/material"
import SettingsIcon from "@mui/icons-material/Settings"
import SendIcon from "@mui/icons-material/Send"
import BlockIcon from "@mui/icons-material/Block"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import VisibilityIcon from "@mui/icons-material/Visibility"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import PeopleIcon from "@mui/icons-material/People"
import WorkIcon from "@mui/icons-material/Work"
import PersonIcon from "@mui/icons-material/Person"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import { ConvertDateTime } from "../../Base/helper/baseFunctions"
import { GetSchemaOwnerUser, GetDashboardMetricsHelper } from "../helper/AdminUserHelper"
import {
  CreatedSchemasGetAction,
  DisableWorkspaceAction,
  EnableWorkspaceAction,
  updatePlanAction,
} from "../../../../store/MeeplAdmin/Workspace/Action"
import { useDispatch, useSelector } from "react-redux"
import SelectableSearch from "../../Base/views/SelectableSearch"
import { PlansAction } from "../../../../store/Plans/Action"
import Popup from "../../Base/views/Popup"
import CommonDatePicker from "../../Base/views/CommonDatePicker"
import ConfirmationDialog from "../../Base/views/ConfirmationDialog"
import CustomTable from "../../Base/views/CustomTable"
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SettingConfigExportPopup from "../helper/SettingConfigExportPopup"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const initialValues = { disable_reason: "" }
const enable_initialValues = { expiry_date: "" }
const initialPlanValues = {
  planid: "",
  group_org_flag: false,
  face_recognition_flag: false,
  plan_day: "",
  payroll_flag: false,
  ats_flag: false,
  visitor_management_flag: false,
  expense_flag: false,
  canteen_management_flag: false,
  canteen_team_size: "",
  fnf_flag: false,
  enable_common_portal: false,
  enable_lms: false,
  whatsapp_flow: false
}

// Helper component for trend indicators
const TrendIndicator = ({ current, previous, type = "number" }) => {
  // Check if previous is a valid number for comparison
  if (typeof previous !== 'number' || current === previous) {
    return null // No indicator for no change or invalid previous data
  }

  const isIncreasing = current > previous
  return isIncreasing ? (
    <TrendingUpIcon sx={{ fontSize: 14, color: "success.main" }} />
  ) : (
    <TrendingDownIcon sx={{ fontSize: 14, color: "error.main" }} />
  )
}
// Compact metric item for cards
const CompactMetricItem = ({ label, value, previousValue, showTrend = true }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
    <Typography variant="caption" sx={{ fontSize: "10px", color: "text.secondary" }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 32, justifyContent: 'flex-end' }}>
      <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 600 }}>
        {value}
      </Typography>
      <Box sx={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {showTrend && <TrendIndicator current={value} previous={previousValue} />}
      </Box>
    </Box>
  </Box>
)

// Version Toggle Component - proper toggle design
const ViewToggle = ({ isTableView, onToggle }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
    <Typography variant="body2" color="text.secondary">
      View:
    </Typography>
    <Box
      sx={{
        display: "flex",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Button
        size="small"
        variant={!isTableView ? "contained" : "text"}
        onClick={() => onToggle(false)}
        sx={{
          minWidth: 60,
          fontSize: "12px",
          borderRadius: 0,
          bgcolor: !isTableView ? "primary.main" : "transparent",
          color: !isTableView ? "white" : "text.primary",
          "&:hover": {
            bgcolor: !isTableView ? "primary.dark" : "action.hover",
          },
        }}
      >
        Cards
      </Button>
      <Button
        size="small"
        variant={isTableView ? "contained" : "text"}
        onClick={() => onToggle(true)}
        sx={{
          minWidth: 60,
          fontSize: "12px",
          borderRadius: 0,
          bgcolor: isTableView ? "primary.main" : "transparent",
          color: isTableView ? "white" : "text.primary",
          "&:hover": {
            bgcolor: isTableView ? "primary.dark" : "action.hover",
          },
        }}
      >
        Table
      </Button>
    </Box>
  </Box>
)

const MetricsPopup = ({ rowData, metrics, open, onClose, loading }) => {
  const [isTableView, setIsTableView] = useState(false)

  // Transform API data to match expected format - UPDATED to match API response
  const transformMetricsData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map(day => ({
      date: day.date ? new Date(day.date).toLocaleDateString('en-GB') : 'N/A',
      active_users: day.active_users || 0,
      inactive_users: day.inactive_users || 0,
      active_jobs: day.active_jobs || 0,
      total_candidates: day.total_candidates || 0,
      verified_candidates: day.verified_candidates || 0,
      unverified_candidates: day.unverified_candidates || 0,
      interviews_scheduled: day.interviews_scheduled || 0,
      payroll_setup_done: day.payroll_setup_done || 0,
      payroll_onhold_employees: day.payroll_onhold_employees || 0,
      // FIX: Properly map payroll month and year
      last_payroll_month: day.last_payroll_generated_month || "N/A",
      payroll_year: day.payroll_year || "N/A",
      recent_visitors: day.recent_visitors || 0,
      // Store flags
      ats_flag: day.ats_flag,
      payroll_flag: day.payroll_flag,
      visitor_management_flag: day.visitor_management_flag,
      }));
    };

  const historicalData = transformMetricsData(metrics).reverse();
  const todayData = historicalData[0] || {}; // First item after reverse = newest
  const yesterdayData = historicalData[1] || {}; // Second item after reverse = previous day
  const atsEnabled = todayData.ats_flag || rowData?.ats_flag
  const payrollEnabled = todayData.payroll_flag || rowData?.payroll_flag
  const visitorManagementEnabled = todayData.visitor_management_flag || rowData?.visitor_management_flag

  // Color scheme for different sections - Minimal light palette
  const sectionColors = {
    "Workspace Level": { bg: "#e8f5e8", headerBg: "#c8e6c9", text: "#2e7d32" }, // Green
    "ATS": { bg: "#e3f2fd", headerBg: "#bbdefb", text: "#1565c0" }, // Blue
    "Payroll": { bg: "#fff3e0", headerBg: "#ffe0b2", text: "#ef6c00" }, // Orange
    "Visitor Management": { bg: "#e8eaf6", headerBg: "#c5cae9", text: "#303f9f" }, // Indigo 
  }

// Workspace Level Metrics - Always Shown
const workspaceMetrics = [
  {
    title: "Active Users",
    value: todayData?.active_users || 0, 
    previousValue: yesterdayData?.active_users || 0,
    key: "active_users",
  },
  {
    title: "Inactive Users",
    value: todayData?.inactive_users || 0, 
    previousValue: yesterdayData?.inactive_users || 0,
    key: "inactive_users",
  }
]

// ATS Metrics
const atsMetrics = atsEnabled ? [
  {
    title: "Total Candidates",
    value: todayData?.total_candidates || 0,
    previousValue: yesterdayData?.total_candidates || 0,
    key: "total_candidates",
  },
  {
    title: "Verified",
    value: todayData?.verified_candidates || 0,
    previousValue: yesterdayData?.verified_candidates || 0,
    key: "verified_candidates",
  },
  {
    title: "Unverified",
    value: todayData?.unverified_candidates || 0,
    previousValue: yesterdayData?.unverified_candidates || 0,
    key: "unverified_candidates",
  },
  {
    title: "Active Jobs",
    value: todayData?.active_jobs || 0, 
    previousValue: yesterdayData?.active_jobs || 0,
    key: "active_jobs",
  },
  {
    title: "Interviews",
    value: todayData?.interviews_scheduled || 0, 
    previousValue: yesterdayData?.interviews_scheduled || 0,
    key: "interviews_scheduled",
  }
] : []

// Payroll Metrics 
const payrollMetrics = payrollEnabled ? [
  {
    title: "Payroll Employees",
    value: todayData?.payroll_setup_done || 0,
    previousValue: yesterdayData?.payroll_setup_done || 0,
    key: "payroll_setup_done",
  },
  {
    title: "On Hold",
    value: todayData?.payroll_onhold_employees || 0, 
    previousValue: yesterdayData?.payroll_onhold_employees || 0,
    key: "payroll_onhold_employees",
  }
] : []

// Visitor Management Metrics 
const visitorMetrics = visitorManagementEnabled ? [
  {
    title: "Total Visitors",
    value: todayData?.recent_visitors || 0,
    previousValue: yesterdayData?.recent_visitors || 0,
    key: "recent_visitors",
  }
] : []

// Check if section has any non-zero data in today's data
  const sectionHasTodayData = (metrics) => {
    return metrics.some(metric => todayData[metric.key] > 0)
  }

  const sectionHasAnyHistoricalData = (metrics) => {
  // Add proper null/undefined checks
  if (!historicalData || !Array.isArray(historicalData) || historicalData.length === 0) {
    return false;
  }
  
  if (!metrics || !Array.isArray(metrics) || metrics.length === 0) {
    return false;
  }

  return historicalData.some(dayData => 
    metrics.some(metric => dayData[metric.key] > 0)
  )
}
  const showATS = atsEnabled
  const showPayroll = payrollEnabled
  const showVisitor = visitorManagementEnabled

  // Check if sections have today's data
  const atsHasTodayData = sectionHasTodayData(atsMetrics)
  const payrollHasTodayData = sectionHasTodayData(payrollMetrics)
  const visitorHasTodayData = sectionHasTodayData(visitorMetrics)

  // Get enabled sections with no data for the common message
  const enabledButNoDataSectionsForCards = [
    { name: "ATS", enabled: atsEnabled, hasData: atsHasTodayData },
    { name: "Payroll", enabled: payrollEnabled, hasData: payrollHasTodayData },
    { name: "Visitor Management", enabled: visitorManagementEnabled, hasData: visitorHasTodayData },
  ].filter(section => section.enabled && !section.hasData)

  const enabledButNoDataSectionsForTable = [
    { 
      name: "ATS", 
      enabled: atsEnabled, 
      hasData: sectionHasAnyHistoricalData(atsMetrics) 
    },
    { 
      name: "Payroll", 
      enabled: payrollEnabled, 
      hasData: sectionHasAnyHistoricalData(payrollMetrics) 
    },
    { 
      name: "Visitor Management", 
      enabled: visitorManagementEnabled, 
      hasData: sectionHasAnyHistoricalData(visitorMetrics) 
    },
  ].filter(section => section.enabled && !section.hasData)

  // Use the appropriate one based on view
  const enabledButNoDataSections = isTableView ? enabledButNoDataSectionsForTable : enabledButNoDataSectionsForCards

  // Show enabled sections even if no data
  const enabledSections = [
    { 
      section: "Workspace Level", 
      metrics: workspaceMetrics,
      icon: <PeopleIcon sx={{ color: sectionColors["Workspace Level"].text, fontSize: 16 }} />,
      enabled: true,
      hasTodayData: sectionHasTodayData(workspaceMetrics)
    },
    { 
      section: "ATS", 
      metrics: atsMetrics,
      icon: <WorkIcon sx={{ color: sectionColors["ATS"].text, fontSize: 16 }} />,
      enabled: atsEnabled,
      hasTodayData: atsHasTodayData
    },
    { 
      section: "Payroll", 
      metrics: payrollMetrics,
      icon: <AccountBalanceIcon sx={{ color: sectionColors["Payroll"].text, fontSize: 16 }} />,
      enabled: payrollEnabled,
      hasTodayData: sectionHasTodayData(payrollMetrics)
    },
    { 
      section: "Visitor Management", 
      metrics: visitorMetrics,
      icon: <PersonIcon sx={{ color: sectionColors["Visitor Management"].text, fontSize: 16 }} />,
      enabled: visitorManagementEnabled,
      hasTodayData: visitorHasTodayData
    },
  ]

  // Check if we have any data at all
  const hasAnyData = historicalData.length > 0 && historicalData.some(day => 
    Object.values(day).some(val => typeof val === 'number' && val > 0)
  )

  // No Data Message Component for Cards
  const NoDataMessage = ({ sectionName }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: 40,
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      borderRadius: 1,
      border: '1px dashed rgba(0, 0, 0, 0.1)',
      mt: 0.5
    }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '10px' }}>
        No data for {sectionName} today
      </Typography>
    </Box>
  )

  return (
    <Popup
      maxWidth={isTableView ? "xl" : "md"}
      title={`Metrics - ${rowData?.company_full_name}`}
      isMasterProduct
      setOpenPopup={onClose}
      openPopup={open}
      fullWidth
    >
      <Box>
        <ViewToggle isTableView={isTableView} onToggle={setIsTableView} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading metrics data...</Typography>
          </Box>
        ) : !hasAnyData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography color="text.secondary">No metrics data available</Typography>
          </Box>
        ) : isTableView ? (
          <Box>
            {/* Common message for enabled but no data sections */}
            {enabledButNoDataSections.length > 0 && (
              <Box sx={{ mb: 2, p: 0.5, backgroundColor: 'rgba(255, 152, 0, 0.08)', borderRadius: 1, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                <Typography variant="body2" sx={{ color: 'warning.dark', fontSize: '12px' }}>
                  <strong>Note:</strong> {enabledButNoDataSections.map(section => section.name).join(', ')} {enabledButNoDataSections.length === 1 ? 'is' : 'are'} enabled but has no data across all {historicalData.length} days.
                </Typography>
              </Box>
            )}
            
            <TableContainer component={Paper} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  {/* Main Headers Row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      backgroundColor: '#f5f5f5',
                    }}>
                      Date
                    </TableCell>
                    
                    {/* Workspace Level Header - ALWAYS SHOWN */}
                    <TableCell 
                      colSpan={workspaceMetrics.length} 
                      align="center"
                      sx={{ 
                        fontWeight: 600, 
                        backgroundColor: sectionColors["Workspace Level"].headerBg, 
                        color: sectionColors["Workspace Level"].text,
                      }}
                    >
                      Workspace Level
                    </TableCell>
                    
                    {/* ATS Section - Only show if has data */}
                    {showATS && (
                      <TableCell 
                        colSpan={atsMetrics.length} 
                        align="center"
                        sx={{ 
                          fontWeight: 600, 
                          backgroundColor: sectionColors["ATS"].headerBg, 
                          color: sectionColors["ATS"].text,
                        }}
                      >
                        ATS
                      </TableCell>
                    )}
                    
                    {/* Payroll Section - Only show if has data */}
                    {showPayroll && (
                      <TableCell 
                        colSpan={payrollMetrics.length} 
                        align="center"
                        sx={{ 
                          fontWeight: 600, 
                          backgroundColor: sectionColors["Payroll"].headerBg, 
                          color: sectionColors["Payroll"].text,
                        }}
                      >
                        Payroll
                      </TableCell>
                    )}
                    
                    {/* Visitor Management Section - Only show if has data */}
                    {showVisitor && (
                      <TableCell 
                        colSpan={visitorMetrics.length} 
                        align="center"
                        sx={{ 
                          fontWeight: 600, 
                          backgroundColor: sectionColors["Visitor Management"].headerBg, 
                          color: sectionColors["Visitor Management"].text,
                        }}
                      >
                        Visitor Management
                      </TableCell>
                    )}
                  </TableRow>
                  
                  {/* Sub-headers row */}
                  <TableRow>
                    <TableCell sx={{ 
                      backgroundColor: '#fafafa', 
                      fontWeight: 500,
                    }}>
                    </TableCell>
                    
                    {/* Workspace sub-headers */}
                    {workspaceMetrics.map((metric, index) => (
                      <TableCell 
                        key={index} 
                        align="center"
                        sx={{ 
                          backgroundColor: sectionColors["Workspace Level"].bg, 
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      >
                        {metric.title}
                      </TableCell>
                    ))}
                    
                    {/* ATS sub-headers */}
                    {showATS && atsMetrics.map((metric, index) => (
                      <TableCell 
                        key={index} 
                        align="center"
                        sx={{ 
                          backgroundColor: sectionColors["ATS"].bg, 
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      >
                        {metric.title}
                      </TableCell>
                    ))}
                    
                    {/* Payroll sub-headers */}
                    {showPayroll && payrollMetrics.map((metric, index) => (
                      <TableCell 
                        key={index} 
                        align="center"
                        sx={{ 
                          backgroundColor: sectionColors["Payroll"].bg, 
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      >
                        {metric.title}
                      </TableCell>
                    ))}
                    
                    {/* Visitor Management sub-headers */}
                    {showVisitor && visitorMetrics.map((metric, index) => (
                      <TableCell 
                        key={index} 
                        align="center"
                        sx={{ 
                          backgroundColor: sectionColors["Visitor Management"].bg, 
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      >
                        {metric.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historicalData.map((dayData, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontWeight: 500, backgroundColor: index === 0 ? "#f8f9fa" : "white", }}>
                        <Typography variant="body2">
                          {dayData.date}
                          {index === 0 && (
                            <Box component="span" sx={{ ml: 0.5, color: "primary.main", fontWeight: 600 }}>
                              (Today)
                            </Box>
                          )}
                        </Typography>
                      </TableCell>
                      
                      {/* Workspace data - ALWAYS SHOWN */}
                      {workspaceMetrics.map((metric, idx) => (
                        <TableCell 
                          key={idx} 
                          align="center"
                          sx={{ 
                            backgroundColor: index === 0 ? "#f1f8e9" : "white",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dayData[metric.key] || 0}
                          </Typography>
                        </TableCell>
                      ))}
                      
                      {/* ATS data - Only show if has data */}
                      {showATS && atsMetrics.map((metric, idx) => (
                        <TableCell 
                          key={idx} 
                          align="center"
                          sx={{ 
                            backgroundColor: index === 0 ? "#e8f5e8" : "#f8fdff",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dayData[metric.key] || 0}
                          </Typography>
                        </TableCell>
                      ))}
                      
                      {/* Payroll data - Only show if has data */}
                      {showPayroll && payrollMetrics.map((metric, idx) => (
                        <TableCell 
                          key={idx} 
                          align="center"
                          sx={{ 
                            backgroundColor: index === 0 ? "#fff8e1" : "#fffaf2",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dayData[metric.key] || 0}
                          </Typography>
                        </TableCell>
                      ))}
                      
                      {/* Visitor Management data - Only show if has data */}
                      {showVisitor && visitorMetrics.map((metric, idx) => (
                        <TableCell 
                          key={idx} 
                          align="center"
                          sx={{ 
                            backgroundColor: index === 0 ? "#e8eaf6" : "#f5f7ff",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dayData[metric.key] || 0}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
) : (
          // Compact Card Version
          <Box>
            {/* Common message for enabled but no data sections */}
            {enabledButNoDataSections.length > 0 && (
              <Box sx={{ mb: 2, p: 0.5, backgroundColor: 'rgba(255, 152, 0, 0.08)', borderRadius: 1, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                <Typography variant="body2" sx={{ color: 'warning.dark', fontSize: '12px' }}>
                  <strong>Note:</strong> {enabledButNoDataSections.map(section => section.name).join(', ')} {enabledButNoDataSections.length === 1 ? 'is' : 'are'} enabled but has no data for today.
                </Typography>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Date: <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>{todayData.date || 'Today'}</Box>
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              {enabledSections
                .filter(section => section.enabled) // Show all enabled sections
                .map((section, sectionIndex) => (
                <Grid item xs={12} sm={6} md={3} key={sectionIndex}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      borderColor: sectionColors[section.section]?.headerBg || "divider",
                      "&:hover": { boxShadow: 1 },
                      transition: "box-shadow 0.2s ease",
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        {section.icon}
                        <Typography variant="body2" sx={{ ml: 1, fontWeight: 600, fontSize: "12px", color: sectionColors[section.section]?.text }}>
                          {section.section}
                        </Typography>
                      </Box>

                      {/* Show metrics if section has today's data, otherwise show no data message */}
                      {section.hasTodayData ? (
                        <>
                          {section.metrics.map((metric, index) => (
                            <CompactMetricItem
                              key={index}
                              label={metric.title}
                              value={metric.value}
                              previousValue={metric.previousValue}
                              showTrend={section.section !== "Visitor Management"}
                            />
                          ))}
                        </>
                      ) : (
                        <NoDataMessage sectionName={section.section} />
                      )}
                      {/* Last Generated info - ALWAYS show for Payroll section when data exists */}
                      {section.section === "Payroll" && todayData.last_payroll_month && todayData.last_payroll_month !== "N/A" && (
                        <Box sx={{ 
                          mt: 0.5, 
                          pt: 0.5, 
                          borderTop: section.hasTodayData ? "1px solid #f0f0f0" : "1px dashed rgba(0, 0, 0, 0.1)" 
                        }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: "9px", 
                              color: "text.secondary",
                            }}
                          >
                            Last Generated: {todayData.last_payroll_month} {todayData.payroll_year || ""}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Popup>
  )
}

const CreatedSchemas = ({ countryCode }) => {
  const dispatch = useDispatch()

  const { CreatedSchemasdata, Loading } = useSelector((state) => state?.MeeplAdminReducer?.WorkspaceReducer)
  const { plansdata } = useSelector((state) => state?.PlansReducer)

  const [plansData, setPlansData] = useState({ load: true, values: [] })
  const [updateplan, setUpdatePlan] = useState(initialPlanValues)
  const [enableplan, setEnablePlan] = useState(false)
  const [disable, setDisable] = useState(false)
  const [disableReason, setDisableReason] = useState(initialValues)
  const [enable, setEnable] = useState(false)
  const [enableData, setEnableData] = useState(enable_initialValues)
  const [currentData, setCurrentData] = useState(null)
  const [inactive, setInactive] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState({ show: false })
  const [showMetrics, setShowMetrics] = useState({ 
    show: false, 
    data: null, 
    metrics: null,
    loading: false 
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedRows, setSelectedRows] = useState([])
  const [selectedRowData, setSelectedRowData] = useState([])
  const [errors, setErrors] = useState({});
  const [exportPopupData, setExportPopupData] = useState(null);
  
  const resetStates = () => {
    setCurrentData(null)
    setDisableReason(initialValues)
    setEnableData(enable_initialValues)
    setUpdatePlan(initialPlanValues)
  }

  // fetch schemas based on active/inactive + plans
  useEffect(() => {
    dispatch(
      CreatedSchemasGetAction({
        is_active: inactive ? "false" : "true",
        country_code: countryCode,
      }),
    )
    dispatch(PlansAction())
  }, [inactive, countryCode, dispatch])

  // update plan + enableData when row selected
  useEffect(() => {
    if (!currentData) return
    setUpdatePlan({
      ...currentData,
      planid: currentData?.planid ? { planid: currentData?.planid, planname: currentData?.planname } : "",
      plan_day: currentData?.expiry_date,
    })
    setEnableData({ ...currentData })
  }, [currentData])

  const handleChange = (event) => {
    setDisableReason({ [event.target.name]: event.target.value })
  }
  const handleEnableChange = (event) => {
    setEnableData({ [event.target.name]: event.target.value })
  }
  const handlePlan = (event, value) => {
    if (
      [
        "group_org_flag",
        "face_recognition_flag",
        "payroll_flag",
        "ats_flag",
        "visitor_management_flag",
        "expense_flag",
        'canteen_management_flag',
        "fnf_flag",
        "enable_common_portal",
        "enable_lms",
        "whatsapp_flow"
      ].includes(event?.target?.name)
    ) {
      const isCanteen = event?.target?.name === "canteen_management_flag";
      const isVisitor = event?.target?.name === "visitor_management_flag";
      const isFaceRecog = event?.target?.name === "face_recognition_flag";
      const isPayroll = event?.target?.name === "payroll_flag";
      const isLMS = event?.target?.name === "enable_lms";
      setUpdatePlan({ ...updateplan, [event.target.name]: event.target.checked,
        ...(isCanteen && {canteen_team_size: ""}),
        ...((isVisitor || isFaceRecog) && {enable_common_portal: false}),
        ...(isLMS && {enable_common_portal: false, face_recognition_flag: false}),
        ...(isPayroll && {enable_lms: event.target.checked ? true : updateplan?.enable_lms}), // payroll is enabled, then they have to take lms
       });
       
       Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({ ...updateplan, [event.target.name]: event.target.checked });
       isCanteen && setErrors({...errors, canteen_team_size: ""})
    } else if (event === "planid") {
      setUpdatePlan({ ...updateplan, [event]: value || "" })
    } else {
      setUpdatePlan({ ...updateplan, [event.target.name]: event.target.value })
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({ ...updateplan, [event.target.name]: event.target.value });
    }
  }
  const validate = (fieldValues) => {
    let temp = { ...errors };
    if ("canteen_team_size" in fieldValues && updateplan?.canteen_management_flag) {
      temp.canteen_team_size =
        !fieldValues?.canteen_team_size ? "Team size is required" : "";
    }
    if ("enable_lms" in fieldValues && fieldValues?.payroll_flag) {
      temp.enable_lms =
        !fieldValues?.enable_lms ? "LMS is required when Payroll is enabled" : "";
    } else {
      temp.enable_lms = "";
    }
    setErrors({
      ...temp,
    });
    return Object.values(temp).every((x) => x === "");
  };
  const handleSubmitPlan = () => {
    if (!validate(updateplan)) return
    dispatch(
      updatePlanAction({
        ...updateplan,
        is_active: inactive ? "false" : "true",
        country_code: countryCode,
      }),
    )
    setEnablePlan(false)
    resetStates()
  }

  const handleDisableWorkspace = () => {
    if (!currentData?.company_id) return
    dispatch(
      DisableWorkspaceAction({
        company_id: currentData.company_id,
        disable_reason: disableReason.disable_reason,
        country_code: countryCode,
      }),
    )
    setDisable(false)
    resetStates()
  }

  const handleEnableWorkspace = () => {
    if (!currentData?.company_id) return
    dispatch(
      EnableWorkspaceAction({
        company_id: currentData.company_id,
        expiry_date: enableData.expiry_date,
        country_code: countryCode,
      }),
    )
    setEnable(false)
    resetStates()
  }

  const handleTableAction = async (action, rowData) => {
    setCurrentData(rowData)
    switch (action) {
      case 'resetPassword':
        setShowResetPassword({
          show: true,
          data: rowData?.domainname,
        })
        break
      case 'disable':
        setDisable(true)
        break
      case 'enable':
        setEnable(true)
        break
      case 'metrics':
        // Fetch real metrics data when opening metrics popup
        setShowMetrics({ 
          show: true, 
          data: rowData, 
          metrics: null,
          loading: true 
        });
        
        try {
          const result = await GetDashboardMetricsHelper(rowData.domainname);
          if (!result.hasError && result.data?.statuscode === 200) {
            setShowMetrics(prev => ({
              ...prev,
              metrics: result.data.data,
              loading: false
            }));
          } else {
            console.error("Failed to fetch metrics:", result.message);
            setShowMetrics(prev => ({
              ...prev,
              metrics: [],
              loading: false
            }));
          }
        } catch (error) {
          console.error("Error fetching metrics:", error);
          setShowMetrics(prev => ({
            ...prev,
            metrics: [],
            loading: false
          }));
        }
        break
      case 'configure':
        setEnablePlan(true)
        break
      case 'export':
        setExportPopupData(rowData)
        break
      default:
        break
    }
  }

  const handleToggleInactive = () => {
    setInactive(!inactive)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Handle row selection from CustomTable
  const handleSelectionChange = (selectedIndices, selectedData) => {
    setSelectedRows(selectedIndices)
    setSelectedRowData(selectedData)
    // You can use this for bulk actions like:
    // - Bulk disable/enable workspaces
    // - Bulk send emails
    // - Bulk update plans
  }

  // Define columns for the table with proper widths for horizontal scroll
  const tableColumns = [
    {
      label: "Company Info",
      field: "company_full_name",
      desktopWidth: '280px', // Increased to accommodate checkbox
      tabletWidth: '240px', 
      mobileWidth: '200px',
      minWidth: '180px',
      render: (rowData) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>
              {rowData.company_full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
              {rowData.email_id}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      label: "Workspace Info",
      field: "domainname",
      desktopWidth: '200px',
      tabletWidth: '160px',
      mobileWidth: '140px',
      minWidth: '130px',
      render: (rowData) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>
            {rowData.domainname}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
            Created: {rowData.createdon ? ConvertDateTime(rowData.createdon) : "N/A"}
          </Typography>
        </Box>
      )
    },
    {
      label: "Team Size",
      field: "team_size",
      desktopWidth: '120px',
      tabletWidth: '100px',
      mobileWidth: '80px',
      minWidth: '70px',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px",}}>
          {rowData?.team_size || 0}
        </Typography>
      )
    },
    {
      label: "Plan & Expiry",
      field: "planname",
      desktopWidth: '200px',
      tabletWidth: '170px',
      mobileWidth: '150px',
      minWidth: '140px',
      render: (rowData) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>
            {rowData.planname}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
            Expires: {rowData.expiry_date ? ConvertDateTime(rowData.expiry_date) : "N/A"}
          </Typography>
        </Box>
      )
    },
    {
      label: "Status",
      field: "isactive",
      desktopWidth: '100px',
      tabletWidth: '90px',
      mobileWidth: '80px',
      minWidth: '70px',
      render: (rowData) => (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 8px',
            borderRadius: '12px',
            backgroundColor: rowData?.isactive === "1" ? '#e8f5e8' : '#ffebee',
            color: rowData?.isactive === "1" ? '#2e7d32' : '#c62828',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          {rowData?.isactive === "1" ? 'Active' : 'Inactive'}
        </Box>
      )
    },
    {
      label: "Actions",
      field: "actions", 
      desktopWidth: '200px',
      tabletWidth: '180px',
      mobileWidth: '160px',
      minWidth: '150px',
      render: (rowData) => (
        <Box sx={{ display: 'flex', justifyContent: 'left', gap: 0.5, flexWrap: 'wrap' }}>
          {tableActions
            .filter(action => !action.show || action.show(rowData))
            .map((action, actionIndex) => (
              <Tooltip key={actionIndex} title={action.tooltip}>
                <IconButton 
                  size="small" 
                  onClick={() => action.onClick(rowData)}
                  color={action.color || "primary"}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
        </Box>
      )
    }
  ]
  // Define actions for the table - with proper conditions
  const tableActions = [
    {
      tooltip: "Reset Password",
      icon: <SendIcon fontSize="small" />,
      color: "primary",
      onClick: (row) => handleTableAction('resetPassword', row),
      show: (row) => row?.isactive === "1" // Only show for active workspaces
    },
    {
      tooltip: "Block Workspace",
      icon: <BlockIcon fontSize="small" />,
      color: "error",
      onClick: (row) => handleTableAction('disable', row),
      show: (row) => row?.isactive === "1" // Only show for active workspaces
    },
    {
      tooltip: "Enable Workspace",
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      color: "success",
      onClick: (row) => handleTableAction('enable', row),
      show: (row) => row?.isactive === "0" // Only show for inactive workspaces
    },
    {
      tooltip: "Export Configured Settings",
      icon: <FileDownloadIcon fontSize="small" />,
      color: "info",
      onClick: (row) => handleTableAction('export', row),
      show: () => true // Always show
    },
    {
      tooltip: "View Metrics",
      icon: <VisibilityIcon fontSize="small" />,
      color: "info",
      onClick: (row) => handleTableAction('metrics', row),
      show: () => true // Always show
    },
    {
      tooltip: "Update Workspace Configuration",
      icon: <SettingsIcon fontSize="small" />,
      color: "success",
      onClick: (row) => handleTableAction('configure', row),
      show: () => true // Always show
    },
  ]

  const tooltipMessages = {
    enable_common_portal:
      "Enables a unified landing page where users can switch between Employee Attendance and Visitor Entry.",
    payroll_flag:
      "This module depends on LMS and cannot be used as a standalone module. Please ensure LMS is enabled to use this feature.",
  };
  return (
    <>
      <Box sx={{ margin: "0px 16px", boxShadow: "0px 2px 4px -1px rgba(158, 158, 158, 0.2), 0px 4px 5px 0px rgba(158, 158, 158, 0.14), 0px 1px 10px 0px rgba(158, 158, 158, 0.12)" }}>
        <CustomTable
          data={CreatedSchemasdata}
          loading={Loading}
          columns={tableColumns}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalCount={CreatedSchemasdata?.length || 0}
          inactive={inactive}
          onToggleInactive={handleToggleInactive}
          showGlobalSearch={true}
          showInactiveToggle={true}
          showColumnSelector={true}
          showRowSelection={true}
          onSelectionChange={handleSelectionChange}
        />
      </Box>

      {/* Disable Popup */}
      <Popup
        maxWidth="sm"
        title="Disable Workspace"
        isMasterProduct
        setOpenPopup={() => {
          setDisable(false)
          resetStates()
        }}
        openPopup={disable}
        fullWidth
      >
        <Box>
          <TextField
            size="small"
            value={disableReason.disable_reason}
            name="disable_reason"
            onChange={handleChange}
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            required
            label="Reason"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={!disableReason.disable_reason}
              onClick={handleDisableWorkspace}
              variant="contained"
              className="mpl-primary-btn"
              sx={{ mt: 2, mr: 1 }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popup>

      {/* Enable Popup */}
      <Popup
        maxWidth="sm"
        title="Enable Workspace"
        isMasterProduct
        setOpenPopup={() => {
          setEnable(false)
          resetStates()
        }}
        openPopup={enable}
        fullWidth
      >
        <Box>
          <FormControl fullWidth>
            <CommonDatePicker
              size="small"
              name="expiry_date"
              label="Expiry Date"
              pickerType="date"
              onKeyDown={(e) => e.preventDefault()}
              pickerFormat="dd/MM/yyyy"
              DefaultDTvalue={enableData?.expiry_date}
              required
              minDate={new Date()}
              handleChange={(value) =>
                handleEnableChange({
                  target: {
                    name: "expiry_date",
                    value: value
                      ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(
                          -2,
                        )}-${("0" + value.getDate()).slice(-2)}`
                      : "",
                  },
                })
              }
            />
          </FormControl>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={!enableData.expiry_date}
              onClick={handleEnableWorkspace}
              variant="contained"
              className="mpl-primary-btn"
              sx={{ mt: 2, mr: 1 }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popup>

      {/* Update Plan Popup */}
      <Popup
        maxWidth="sm"
        title="Update Workspace Configuration"
        isMasterProduct
        setOpenPopup={() => {
          setEnablePlan(false)
          resetStates()
        }}
        openPopup={enableplan}
        fullWidth
      >
        <Box py={1}>
          <Grid container px={1}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SelectableSearch
                  isOptionEqualToValue={(o, v) => +o.planid === +v.planid}
                  filterOptions="planid"
                  keys={["planid", "planname"]}
                  label="Plans"
                  required
                  value={updateplan?.planid}
                  loading={() => setPlansData({ load: false, values: plansdata || [] })}
                  fieldLabel="planname"
                  variant="contained"
                  name="planid"
                  size="small"
                  data={plansData}
                  onChangeEvent={handlePlan}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.planname} - ₹ {option.plancost}
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} pt={2} pb={1}>
              <FormControl fullWidth>
                <CommonDatePicker
                  size="small"
                  name="plan_day"
                  label="Expiry Date"
                  pickerType="date"
                  onKeyDown={(e) => e.preventDefault()}
                  pickerFormat="dd/MM/yyyy"
                  DefaultDTvalue={updateplan?.plan_day}
                  required
                  minDate={new Date()}
                  handleChange={(value) =>
                    handlePlan({
                      target: {
                        name: "plan_day",
                        value: value
                          ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(
                              -2,
                            )}-${("0" + value.getDate()).slice(-2)}`
                          : "",
                      },
                    })
                  }
                />
              </FormControl>
            </Grid>

            {/* Feature toggles */}
            {[
              ["group_org_flag", "Enable Group Organisation flow?"],
              ["enable_corehr", "Enable CoreHR?"],
              ["enable_lms", "Enable LMS?"],
              ["payroll_flag", "Enable Payroll?"],
              ...(updateplan?.enable_lms ? [["face_recognition_flag", "Enable Face Recognition?"]] : []),
              ["visitor_management_flag", "Enable Visitor Management?"],
              ...(updateplan?.face_recognition_flag && updateplan?.visitor_management_flag && updateplan?.enable_lms ? [["enable_common_portal", "Enable Common Portal?"]] : []),
              ["ats_flag", "Enable ATS?"],
              ["expense_flag", "Enable Expense Management?"],
              ["fnf_flag", "Enable Exit Management?"],
              ["whatsapp_flow", "Enable WhatsApp Flow?"],
            ].map(([name, label]) => (
              <Box key={name}>
                <FormControlLabel
                  control={<Checkbox name={name} checked={updateplan?.[name] || false} onChange={handlePlan} />}
                  disabled={name === "enable_corehr"}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{label}</span>
  
                      {(name === "enable_common_portal" || name === "payroll_flag") && (
                        <Tooltip
                          title={tooltipMessages[name]}
                          arrow
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color: "#777",
                              cursor: "pointer",
                              mb: 0.2
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
                {errors?.[name] && (
                  <FormHelperText error sx={{ ml: 0.5, mt: -0.5, fontSize: "12px" }}>
                    {errors[name]}
                  </FormHelperText>
                )}
              </Box>
            ))}
          </Grid>
          <Grid container item xs={12} px={1}
            sx={{ 
            ...(updateplan?.canteen_management_flag && {
                border: "1px solid #e0e0e0",
                borderRadius: "5px",
                pt: "8px",
                pb: "2px",
              })}}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormControlLabel
                control={<Checkbox name='canteen_management_flag' checked={updateplan?.canteen_management_flag || false} onChange={handlePlan} />}
                label={'Enable Canteen Management?'}
              />
              </FormControl>
            </Grid>
           { updateplan?.canteen_management_flag && (
            <Grid item xs={12} md={6} pt={0.5}>
              <FormControl fullWidth>
                <TextField
                    label="Canteen Management Team Size"
                    fullWidth
                    required
                    name="canteen_team_size"
                    onChange={handlePlan}
                    value={updateplan?.canteen_team_size || ""}
                    size="small"
                    type="number"
                    {...(errors.canteen_team_size && {
                      error: true,
                      helperText: errors.canteen_team_size,
                    })}
                  />
              </FormControl>
            </Grid>)}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={!updateplan.planid || !updateplan.plan_day}
              onClick={handleSubmitPlan}
              variant="contained"
              className="mpl-primary-btn"
              sx={{ mt: 2, mr: 1 }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popup>

      {/* Metrics Popup */}
      <MetricsPopup
        rowData={showMetrics.data}
        metrics={showMetrics.metrics}
        open={showMetrics.show}
        onClose={() => setShowMetrics({ show: false, data: null, metrics: null, loading: false })}
        loading={showMetrics.loading}
      />

      {/* Export Popup */}
      <Popup
        title="Export Configured Settings"
        isMasterProduct
        setOpenPopup={() => {
          setExportPopupData(null)
        }}
        openPopup={exportPopupData}
      >
        <SettingConfigExportPopup  exportPopupData={exportPopupData} setExportPopupData={setExportPopupData}/>
      </Popup>

      {/* Reset Password */}
      <ConfirmationDialog
        title="Reset Password"
        content="Are you sure you want to send a reset password email?"
        openDialog={showResetPassword?.show}
        closeDialog={setShowResetPassword}
        popupTitle="Users"
        CancelButtonText="Cancel"
        OkButtonText="Send Email"
        onCancel={() => setShowResetPassword({ show: false })}
        onSubmit={() => {
          setShowResetPassword({ show: false })
          GetSchemaOwnerUser({ domain_name: showResetPassword?.data })
        }}
      />
    </>
  )
}

export default CreatedSchemas